import { useEffect, useRef } from "react";

// Pointer-driven 3D tilt with spring easing, plus a foil glare that drifts
// opposite the tilt. Written straight to the DOM inside a rAF loop, so a
// moving pointer never re-renders React.
//
// Pointer: rotation follows the pointer's position inside the card.
// Touch: rotation follows swipe momentum, and springs back on release.
//
// Disabled (reduced motion), no listeners are attached at all. At rest the
// inline transform is cleared, so the card returns to its stylesheet state.
export function useTilt<T extends HTMLElement>(enabled: boolean, maxTilt = 11) {
  const cardRef = useRef<T | null>(null);
  const glareRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || !enabled) return;

    let rotX = 0;
    let rotY = 0;
    let velX = 0;
    let velY = 0;
    let targetX = 0;
    let targetY = 0;
    let hovering = false;
    let frame = 0;
    let last = performance.now();
    let lastTouchX: number | null = null;

    const rest = () => {
      rotX = rotY = velX = velY = 0;
      card.style.transform = "";
      const glare = glareRef.current;
      if (glare) {
        glare.style.opacity = "0";
        glare.style.transform = "";
      }
    };

    const render = (now: number) => {
      const dt = Math.min(32, now - last) / 16.67;
      last = now;

      const stiffness = 0.085;
      const damping = Math.pow(0.84, dt);

      velX += (targetX - rotX) * stiffness * dt;
      velY += (targetY - rotY) * stiffness * dt;
      velX *= damping;
      velY *= damping;
      rotX += velX * dt;
      rotY += velY * dt;

      card.style.transform = `perspective(1000px) rotateX(${rotX.toFixed(2)}deg) rotateY(${rotY.toFixed(2)}deg)`;

      const glare = glareRef.current;
      if (glare) {
        const intensity = Math.min(0.5, (Math.abs(rotX) + Math.abs(rotY)) / 26);
        glare.style.transform = `translate3d(${(-rotY * 2.6).toFixed(1)}px, ${(rotX * 2.6).toFixed(1)}px, 0)`;
        glare.style.opacity = intensity.toFixed(3);
      }

      const settled =
        !hovering &&
        Math.abs(velX) < 0.02 &&
        Math.abs(velY) < 0.02 &&
        Math.abs(targetX - rotX) < 0.05 &&
        Math.abs(targetY - rotY) < 0.05;

      if (settled) {
        rest();
        frame = 0;
        return;
      }
      frame = requestAnimationFrame(render);
    };

    const ensureLoop = () => {
      if (!frame) {
        last = performance.now();
        frame = requestAnimationFrame(render);
      }
    };

    const clamp = (value: number, limit: number) => Math.max(-limit, Math.min(limit, value));

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === "touch") {
        if (lastTouchX === null) {
          lastTouchX = event.clientX;
          return;
        }
        const delta = event.clientX - lastTouchX;
        lastTouchX = event.clientX;
        targetY = clamp(targetY + delta * 0.7, maxTilt * 1.8);
        targetX = clamp(targetX - delta * 0.25, maxTilt * 1.2);
      } else {
        hovering = true;
        const rect = card.getBoundingClientRect();
        const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const ny = ((event.clientY - rect.top) / rect.height) * 2 - 1;
        targetY = clamp(nx * maxTilt, maxTilt * 1.4);
        targetX = clamp(-ny * maxTilt, maxTilt * 1.4);
      }
      ensureLoop();
    };

    const release = () => {
      hovering = false;
      lastTouchX = null;
      targetX = 0;
      targetY = 0;
      ensureLoop();
    };

    const onPointerDown = () => {
      hovering = true;
      lastTouchX = null;
      ensureLoop();
    };

    card.addEventListener("pointermove", onPointerMove);
    card.addEventListener("pointerleave", release);
    card.addEventListener("pointerdown", onPointerDown);
    card.addEventListener("pointerup", release);
    card.addEventListener("pointercancel", release);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      card.removeEventListener("pointermove", onPointerMove);
      card.removeEventListener("pointerleave", release);
      card.removeEventListener("pointerdown", onPointerDown);
      card.removeEventListener("pointerup", release);
      card.removeEventListener("pointercancel", release);
      rest();
    };
  }, [enabled, maxTilt]);

  return { cardRef, glareRef };
}
